import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Feather, Check } from "lucide-react";

export default function SoulLetterEditor({ isOpen, onClose, onSend }) {
    const [content, setContent] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [selectedDelay, setSelectedDelay] = useState(0);

    const DELAY_OPTIONS = [
        { label: "Deliver Now", value: 0 },
        { label: "In 1 Hour", value: 3600 },
        { label: "In 8 Hours", value: 28800 },
        { label: "In 24 Hours", value: 86400 }
    ];

    const handleSend = async () => {
        if (!content.trim()) return;
        setIsSending(true);
        // Simulate deliberate slowness for "Soul" feel
        await new Promise(r => setTimeout(r, 1000));
        const payload = JSON.stringify({
            text: content,
            unlockAt: Date.now() + (selectedDelay * 1000)
        });
        onSend(payload);
        setContent("");
        setIsSending(false);
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[60] bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 20 }}
                        className="w-full max-w-2xl h-[80vh] bg-paper-pattern dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl flex flex-col relative overflow-hidden"
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/20 rounded-full flex items-center justify-center text-indigo-500">
                                    <Feather className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-display font-bold text-gray-800 dark:text-white">Soul Letter</h2>
                                    <p className="text-xs text-gray-500">Take your time. No rush.</p>
                                </div>
                            </div>
                            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                                <X className="w-6 h-6 text-gray-400" />
                            </button>
                        </div>

                        {/* Editor */}
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="Dear friend..."
                            className="flex-1 w-full p-8 text-lg leading-relaxed text-gray-800 dark:text-gray-200 bg-transparent resize-none focus:outline-none font-serif placeholder:italic"
                            autoFocus
                        />

                        {/* Delivery Options */}
                        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">When should this be revealed?</p>
                            <div className="flex flex-wrap gap-2">
                                {DELAY_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setSelectedDelay(opt.value)}
                                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedDelay === opt.value
                                                ? "bg-indigo-100 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700/50 shadow-sm"
                                                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
                                            }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex justify-end gap-3">
                            <button onClick={onClose} className="px-6 py-3 text-gray-500 hover:text-gray-700 font-medium text-sm">
                                Discard
                            </button>
                            <button
                                onClick={handleSend}
                                disabled={!content.trim() || isSending}
                                className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full font-medium text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-indigo-200 dark:shadow-none"
                            >
                                {isSending ? (
                                    <>Sending...</>
                                ) : (
                                    <>Send Letter <Send className="w-4 h-4" /></>
                                )}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
